















import { Component, Prop, Vue } from 'vue-property-decorator';
import { Story, statusType, privacyType } from '@/Story'
import * as utils from '@/utils';
import * as utils_auth from '@/utils_auth';
import { globalUC } from '@/UserCache'
import { StoryUser, howJoinedTypes, likelyToBeTypes, quizSteps, quizStepAnswer } from '@/StoryUser'
import { setIntervalAsync, SetIntervalAsyncTimer } from 'set-interval-async/fixed'    // a little better than calling regular setInterval when using async/await, because this offers safety from overlapping calls - https://github.com/ealmansi/set-interval-async
import { clearIntervalAsync } from 'set-interval-async'

/* dynamic way */
// import QuizChooseLearningMethodComponent from '@/components/quiz/QuizChooseLearningMethodComponent.vue';
// const QuizChooseLearningMethodComponent = () => import('@/components/quiz/QuizChooseLearningMethodComponent.vue')

/* static import way */
import QuizUniversalComponent from '@/components/quiz/QuizUniversalComponent.vue';

// we use dynamic components to swap out which component is shown
//  * https://vuejs.org/v2/guide/components.html#Dynamic-Components
//  * https://stackoverflow.com/questions/43658481/passing-props-dynamically-to-dynamic-component-in-vuejs

const quizComponents = {
  welcome: QuizUniversalComponent,
}

// TODO: event tracking in telegram
// TODO: allow inviting other listeners to storyTeller's circle

interface subjectWords { normal: string, possessive: string, pronoun: string, pronounPossessive: string }

@Component({
  name: 'QuizComponent',
  components: { 
    QuizUniversalComponent,
  }
})
export default class QuizComponent extends Vue {
  // prop reactive member variables publically available to the html template

  // public reactive member variables available to the html template
  public modalShow: boolean = false
  public modalSize: string = "md"
  public modalTitle: string = null
  public stepHistory: quizSteps[] = []
  public stepShowing: quizSteps = null
  public activeQuizComponent = null
  public inReadState: boolean = false
  public quizStepAnswers: { [ quizStep: string ]: quizStepAnswer } = {}

  public image: string = null
  public title: string = null
  public lineTexts: string[] = null
  public choices: string[] = null
  public inputs: string[] = null
  public allowMultiple: boolean = false
  public phoneInput: boolean = false
  public passwordInput: boolean = false
  public allowSkip: number = -1     // -1 means no skip allowed, 0 means skip allowed immediately, otherwise skip shows up in X ms

  // public functions available to the html template
  public get loading(): boolean { return this.$store.state.loading; }
  public get user(): StoryUser { return this.$store.getters.userObject }
  public get userId(): string { return this.$store.getters.userId }
  public get isAuthenticated(): StoryUser { return this.$store.getters.isAuthenticated }
  public get inQuiz(): boolean { return this.$store.state.inQuiz; }
  public get showXButton(): boolean { return this.stepShowing !== 'welcome'}

  public xButton() {
    (window as any).analytics.track(`Quiz Exited`);
    this.exitQuiz()
  }

  public async onBack(step: quizSteps) {
    if (this.$store.state.devmode) console.log(`${this.$options.name} onBack(${step})`)

    const lastStep: quizSteps = this.stepHistory.length > 1 ? this.stepHistory[this.stepHistory.length - 2] : null

    this.quizStepAnswers[lastStep] = { 
      quizStep: lastStep,
      completed: false,
      skipped: false,
      answers: null
    };

    (window as any).analytics.track(`Quiz ${step} Back`);


    this.stepHistory.pop()

    this.lastQuizActivityTime = new Date()
    await this.readStateAndPathAndShowCorrectStep()
  }

  public async onStepSkipped(step: quizSteps) {
    if (this.$store.state.devmode) console.log(`${this.$options.name} onStepSkipped(${step})`)
    this.quizStepAnswers[step] = { 
      quizStep: step,
      completed: false,
      skipped: true,
      answers: null
    };

    (window as any).analytics.track(`Quiz ${step} Skipped`);

    this.lastQuizActivityTime = new Date()
    await this.readStateAndPathAndShowCorrectStep()
  }
  public async onStepDone(step: quizSteps, answers: string[], password: string) {
    if (this.$store.state.devmode) console.log(`${this.$options.name} onStepDone(${step})`)
    this.quizStepAnswers[step] = {
      quizStep: step,
      completed: true,
      skipped: false,
      answers
    };

    (window as any).analytics.track(`Quiz ${step} Done`, { answers });

    // register the new account
    if (step === 'register' && password) {
      let firstName: string
      let lastName: string
      let email: string

      if (answers.length === 1) {
        // email only, and so firstName comes from storyTellerName step
        email = answers[0]
        firstName = this.quizStepAnswers['storyTellerName'].answers[0]
        lastName = this.quizStepAnswers['storyTellerName'].answers[1]
      } else {
        firstName = answers[0]
        lastName = answers[1]
        email = answers[2]
      }

      // tidy up fields
      firstName = utils.capitalizeFirstLetter(firstName.trim())
      lastName = utils.capitalizeFirstLetter(lastName.trim())
      email = email.trim()
      password = password.trim()

      // get utm parameters in case we have them captured by App, Home, HomeAnonymous, Blog or Lander.vue
      const utm_source: string = localStorage.getItem('utm_source')
      const utm_campaign: string = localStorage.getItem('utm_campaign')
      const utm_medium: string = localStorage.getItem('utm_medium')
      const utm_content: string = localStorage.getItem('utm_content')
      const utm_term: string = localStorage.getItem('utm_term')

      // make POST call to server
      // TODO: send answers to API as well, so that server can not only store it with the user, but also automatically
      //  process the invite as well that is inside the answers package.
      // TODO: make the tutorial flow well after this.
      let userRegisteredSuccessfully: boolean = null
      try {
        this.$store.commit('setLoading', { loading: true });
        const url: string = utils.getURLprefix() + '/api/auth/register'
        const response: any = await utils.makePostCall(url, { firstName, lastName, email, phone: null, password, password2: password, quizStepAnswers: this.quizStepAnswers, utm_source, utm_campaign, utm_medium, utm_content, utm_term } );
        if (!response || !response.data) throw(new Error('QuizComponent.onStepDone() got null response or null response.data from /api/auth/register'))
        if (response.data.error) {
          this.$store.commit('setLoading', { loading: false });
          utils.toast(this, 'Error', response.data.error)
          userRegisteredSuccessfully = false
        }
        if (response.data.user) {
          // success
          userRegisteredSuccessfully = true

          const user: StoryUser = new StoryUser(response.data.user)
          utils_auth.saveAuthStuffToStoreAndLocalStorage(user, response.data.sessionId)

          // @ts-ignore: because segment analytics is loaded in head script
          window.analytics.alias(user.getIdAsString())  // tie the previously anon user to this newly id'd user

          // @ts-ignore: because segment analytics is loaded in head script
          window.analytics.track('User Registered')

          // go to homepage - wrap in try/catch to avoid NavigationDuplicated error
          try { await this.$router.push({ name: 'home'}) } catch (Error) {}

          // refresh browser to start tutorial
          this.$store.commit('setInTutorial', { inTutorial: true });
          await this.$router.go(0)
        }

      } catch (error) {
        utils.toast(this, 'System Error', 'Could not register')
        if (this.$store.state.devmode) console.error(error);
        userRegisteredSuccessfully = false
      }

      // if quiz answers includes invitation to a storyTeller, let's send invite to the storyTeller
      if (userRegisteredSuccessfully && !this.isYou()) {
        // make POST call to server
        try {
          const storyTellerFirstName: string = utils.capitalizeFirstLetter(this.quizStepAnswers['storyTellerName'].answers[0].trim())
          const storyTellerLastName: string = utils.capitalizeFirstLetter(this.quizStepAnswers['storyTellerName'].answers[1].trim())
          const storyTellerEmail: string = this.quizStepAnswers['storyTellerEmail'].answers[0].trim().toLocaleLowerCase()
          const storyTellerPhone: string = this.quizStepAnswers['storyTellerPhoneNumber'].answers ? this.quizStepAnswers['storyTellerPhoneNumber'].answers[0] : null

          this.$store.commit('setLoading', { loading: true });
          const url: string = utils.getURLprefix() + '/api/users/invite';
          const response: any = await utils.makePostCall(url, { alsoInviteStoryTeller: true, storyTellerFirstName, storyTellerLastName, storyTellerEmail, storyTellerPhone, firstNames: [], lastNames: [], emails: [] } );
          if (!response || !response.data) throw(new Error('QuizComponent.onStepDone() got null response or null response.data from /api/auth/invite'))
          if (response.data.error) {
            this.$store.commit('setLoading', { loading: false });
            utils.toast(this, 'Error', response.data.error)
          }
          if (response.data.user && response.data.users) {
            // success
            const numInvitesSent: number = response.data.users.length + 1

            // might as well update our user cache
            // TODO: consider adding all the invited users to cache - nah, not really worth it
            const me: StoryUser = new StoryUser(response.data.user)
            me.updateMeToCacheStoreAndLocalStorage()
          }

        } catch (error) {
          utils.toast(this, 'System Error', 'Could not invite')
          if (this.$store.state.devmode) console.error(error);
        } finally {
          this.$store.commit('setLoading', { loading: false });
        }
      }

      this.$store.commit('setLoading', { loading: false });
    }

    this.lastQuizActivityTime = new Date()
    await this.readStateAndPathAndShowCorrectStep()
  }
  public exitQuiz(): void {
    if (this.timerRecurring) clearIntervalAsync(this.timerRecurring)
    this.timerRecurring = null
    this.modalShow = false
    this.$store.commit("setInQuiz", { inQuiz: false });     // this will get this component destroyed!
  }

  // private, non-reactive member variables
  private timerRecurring: SetIntervalAsyncTimer
  private lastQuizActivityTime: Date = null

  // private functions not available directly to HTML template
  private async mounted() {
    if (this.$store.state.devmode) console.log(`${this.$options.name} mounted()`)

    // @ts-ignore: because segment analytics is loaded in head script
    if (this.user) window.analytics.identify(this.user.getIdAsString(), this.user.getTraitObjectForSegment());

    this.lastQuizActivityTime = new Date()
    await this.readStateAndPathAndShowCorrectStep()

    // start polling task that reads state of user to figure out what step to show
    // we poll in case user is on multiple devices (like Juan was), and also that's how recordForTutorial auto proceeds
    // this.timerRecurring = setIntervalAsync(async function() {
    //   await this.readStateAndPathAndShowCorrectStep()
    // }.bind(this), 3000)

  }
  private async beforeDestroy() {
    if (this.$store.state.devmode) console.log(`${this.$options.name} beforeDestroy()`)

    if (this.timerRecurring) clearIntervalAsync(this.timerRecurring)
    this.timerRecurring = null
  }

  private isDone(step: quizSteps) { return this.quizStepAnswers[step] && this.quizStepAnswers[step].completed }
  private isSkipped(step: quizSteps) { return this.quizStepAnswers[step] && this.quizStepAnswers[step].skipped }
  private isDoneOrSkipped(step: quizSteps) { return this.isDone(step) || this.isSkipped(step) }

  // BELOW IS WHERE ALL THE QUIZ BRANCHING LOGIC IS
  // TODO: Create a json-based language to describe a complete quiz - might not ever be worth it

  /*
  export type quizMain = 'welcome'|'chooseLearningMethod'|'whoIsStoryTeller'
  export type quizDemographics = 'gender'|'age'|'speaksEnglish'|'language'|'unitedStates'|'immigrant'|'children'|'grandchildren'
  export type quizTechnology = 'howCaptureNow'|'technologyKnowledge'|'phoneKind'|'textMessage'|'useEmail'|'whiteGlove'
  export type quizFeatures = 'features'|'freeOrPaid'
  export type quizPersonality = 'naturallyTalkative'|'senseOfHumour'
  export type quizInterests = 'college'|'sports'|'religious'|'pets'
  export type quizMisc = 'promptVoice'|'promptFrequency'
  export type quizInvite = 'storyTellerName'|'storyTellerEmail'|'storyTellerPhoneNumber'
  export type quizRegister = 'register'
  */

  private isParent(): boolean {
    if (this.quizStepAnswers['whoIsStoryTeller'] && this.quizStepAnswers['whoIsStoryTeller'].answers) {
      return (this.quizStepAnswers['whoIsStoryTeller'].answers.includes('One of my parents'))
    }
    return false
  }
  private isYou(): boolean {
    if (this.quizStepAnswers['whoIsStoryTeller'] && this.quizStepAnswers['whoIsStoryTeller'].answers) {
      return (this.quizStepAnswers['whoIsStoryTeller'].answers.includes('My own'))
    }
    return false
  }
  private isChild(): boolean {
    if (this.quizStepAnswers['whoIsStoryTeller'] && this.quizStepAnswers['whoIsStoryTeller'].answers) {
      return (this.quizStepAnswers['whoIsStoryTeller'].answers.includes('One of my children'))
    }
    return false
  }
  private isSomeoneElse(): boolean {
    if (this.quizStepAnswers['whoIsStoryTeller'] && this.quizStepAnswers['whoIsStoryTeller'].answers) {
      return (this.quizStepAnswers['whoIsStoryTeller'].answers.includes('Someone else'))
    }
    return false
  }
  private isMale(): boolean {
    if (this.quizStepAnswers['gender'] && this.quizStepAnswers['gender'].answers) {
      return (this.quizStepAnswers['gender'].answers.includes('Male'))
    }
    return false
  }
  private isFemale(): boolean {
    if (this.quizStepAnswers['gender'] && this.quizStepAnswers['gender'].answers) {
      return (this.quizStepAnswers['gender'].answers.includes('Female'))
    }
    return false
  }
  private speaksEnglish(): boolean {
    if (this.quizStepAnswers['speaksEnglish'] && this.quizStepAnswers['speaksEnglish'].answers) {
      return (this.quizStepAnswers['speaksEnglish'].answers.includes('Yes'))
    }
    return false
  }
  private firstName(): string {
    if (this.quizStepAnswers['storyTellerName'] && this.quizStepAnswers['storyTellerName'].answers) {
      return this.quizStepAnswers['storyTellerName'].answers[0]
    }
    return null
  }
  private olderThan18(): boolean {
    if (this.quizStepAnswers['age'] && this.quizStepAnswers['age'].answers) {
      if (this.quizStepAnswers['age'].answers.includes('Less than 18')) return false
      if (this.quizStepAnswers['age'].answers.includes('Less than 10')) return false
      if (this.quizStepAnswers['age'].answers.includes('10 - 17')) return false
    }
    return true
  }
  private olderThan50(): boolean {
    if (this.quizStepAnswers['age'] && this.quizStepAnswers['age'].answers) {
      if (this.quizStepAnswers['age'].answers.includes('Less than 18')) return false
      if (this.quizStepAnswers['age'].answers.includes('Less than 10')) return false
      if (this.quizStepAnswers['age'].answers.includes('10 - 17')) return false
      if (this.quizStepAnswers['age'].answers.includes('18 - 30')) return false
      if (this.quizStepAnswers['age'].answers.includes('30 - 50')) return false
    }
    return true
  }
  private hasChildren(): boolean {
    if (this.quizStepAnswers['children'] && this.quizStepAnswers['children'].answers) {
      if (this.quizStepAnswers['children'].answers.includes('0')) return false
    }
    return true
  }
  private isTechnicalExpert(): boolean {
    if (this.quizStepAnswers['technologyKnowledge'] && this.quizStepAnswers['technologyKnowledge'].answers) {
      if (this.quizStepAnswers['technologyKnowledge'].answers.includes('Expert')) return true
    }
    return false
  }
  private isTechnicalChallenged(): boolean {
    if (this.quizStepAnswers['technologyKnowledge'] && this.quizStepAnswers['technologyKnowledge'].answers) {
      if (this.quizStepAnswers['technologyKnowledge'].answers.includes('Almost none!')) return true
    }
    return false
  }


  private getSubject(): subjectWords {
    let normal: string
    let possessive: string
    let pronoun: string
    let pronounPossessive: string
    const parent: boolean = this.isParent()
    const child: boolean = this.isChild()
    const male: boolean = this.isMale()
    const female: boolean = this.isFemale()
    const someoneElse: boolean = this.isSomeoneElse()
    const firstName: string = this.firstName()

    normal = 'you'; possessive = 'your'; pronoun = 'you'; pronounPossessive = 'your'
    if (parent) {
      normal = "your parent"; possessive = "your parent's"; pronoun = 'they'; pronounPossessive = 'their'
      if (male) { normal = "your father"; possessive = "your father's"; pronoun = 'he'; pronounPossessive = 'his' }
      if (female) { normal = "your mother"; possessive = "your mother's"; pronoun = 'she'; pronounPossessive = 'her' }
    }
    if (child) {
      normal = "your child"; possessive = "your child's"; pronoun = 'they'; pronounPossessive = 'their'
      if (male) { normal = "your son"; possessive = "your son's"; pronoun = 'he'; pronounPossessive = 'his' }
      if (female) { normal = "your daughter"; possessive = "your daughter's"; pronoun = 'she'; pronounPossessive = 'her' }
    }
    if (someoneElse) {
      normal = "them"; possessive = "their"; pronoun = 'they'; pronounPossessive = 'their'
      if (male) { normal = "he"; possessive = "his"; pronoun = 'he'; pronounPossessive = 'his' }
      if (female) { normal = "she"; possessive = "her"; pronoun = 'she'; pronoun = 'she'; pronounPossessive = 'her' }
    }
    if (firstName && !this.isYou()) {
      normal = firstName
      possessive = firstName + "'s"
    }

    return { normal, possessive, pronoun, pronounPossessive }
  }
  private convertToMe(s: string): string {
    if (s === 'you') return 'me'
    s = s.replace('your', 'my')
    return s
  }

  private static getQuizWelcomeImageBasedOnAd(): string {
    let image: string = 'grandma-granddaughter-sofa-512-200-50.jpg'

    const utm_content: string = localStorage.getItem('utm_content')
    if (utm_content) {
      if (utm_content.includes('asian-grandfather-and-grandson')) image = 'asian-granddad-512x175-60.jpg'
      if (utm_content.includes('elderly-couple-close-bw')) image = 'elderly-couple-close-bw-512-200-50.jpg'
      if (utm_content.includes('grandma-granddaughter-sofa')) image = 'grandma-granddaughter-sofa-512-200-50.jpg'
      if (utm_content.includes('old-man-hat-gray')) image = 'old-man-hat-gray-512-200-50.jpg'
    }

    return image
  }

  private setQuizProps(step: quizSteps) {
    this.modalSize = 'md'
    this.image = null
    this.title = null
    this.lineTexts = null
    this.choices = null
    this.inputs = null
    this.allowMultiple = false
    this.phoneInput = false
    this.passwordInput = false
    this.allowSkip = -1

    const subject: subjectWords = this.getSubject()

    switch (step) {
      case 'welcome':
        this.image = QuizComponent.getQuizWelcomeImageBasedOnAd()
        this.title = 'Welcome!'
        this.lineTexts = ["<b>StoryCircle</b> lets you preserve and share your life memories in <b>your own voice</b> with your private <b>circle</b> of loved ones.", "Recording a memory is as easy as <b>leaving a voicemail message</b>, and you don't need to learn or install an app."]
        break
      case 'chooseLearningMethod':
        this.title = 'How best do you learn?'
        this.choices = ['Watching a video', 'Reading', 'I already know how StoryCircle works!', "Skip. I'll learn as I go."]
        this.allowMultiple = true
        break
      case 'whoIsStoryTeller':
        this.title = 'Whose memories are you looking to preserve?'
        this.choices = ['One of my parents', 'My own', /*'One of my children',*/ 'Someone else']
        break
      case 'gender':
        this.title = `What is ${subject.possessive} gender?`
        this.choices = ['Male', 'Female']
        break
      case 'age':
        this.title = `What is ${subject.possessive} age?`
        this.choices = ['Less than 18', '18 - 30', '30 - 50', '51 - 70', 'Over 70']
        if (this.isParent()) this.choices = ['Less than 30', '30 - 50', '51 - 70', 'Over 70']
        if (this.isChild()) this.choices = ['Less than 10', '10 - 17', '18 - 30', '30 - 50', 'Over 50']
        break
      case 'why':
        this.title = `What's the main reason you want to preserve ${subject.possessive} memories?`
        if (this.isParent()) {
          this.choices = [
          `To learn more about ${this.convertToMe(subject.possessive)} life.`,
          `To strengthen my relationship with ${this.convertToMe(subject.normal)}.`,
          `To preserve ${this.convertToMe(subject.possessive)} legacy.`,
          `To listen to ${this.convertToMe(subject.possessive)} stories in ${this.convertToMe(subject.pronounPossessive)} voice after ${this.convertToMe(subject.pronoun)} passes.`,
          'Other'
          ]
        }
        if (this.isSomeoneElse()) {
          this.choices = [
          `To learn more about ${this.convertToMe(subject.possessive)} life.`,
          `To strengthen our current relationship.`,
          `To preserve ${this.convertToMe(subject.possessive)} legacy.`,
          `To listen to ${this.convertToMe(subject.possessive)} stories in ${this.convertToMe(subject.pronounPossessive)} voice after ${this.convertToMe(subject.pronoun)} passes.`,
          'Other'
          ]
        }
        if (this.isChild()) {
          this.choices = [
          `To remember what my child's voice sounds like when ${subject.pronoun} was young.`,
          `To strengthen my relationship with ${this.convertToMe(subject.normal)}.`,
          `I want my kids to remember what it was like when they were young.`,
          'Other'
          ]
        }
        if (this.isYou()) {
          this.choices = [
          `To preserve and share my legacy with loved ones.`,
          `To strengthen my relationships with my loved ones.`,
          `I want loved ones to have my memories in my voice after I pass.`,
          `To keep a personal diary or journal.`,
          'Other'
          ]
        }
        break;
      case 'speaksEnglish':
        if (this.isYou()) {
          this.title = 'Do you speak English?'
        } else {
          this.title = `Does ${subject.normal} speak English?`
        }
        this.choices = ['Yes', 'No']
        break
      case 'language':
        this.title = `What is ${subject.possessive} first language?`
        this.choices = ['Spanish', 'Mandarin Chinese', 'Cantonese', 'Vietnamese', 'French', 'Korean', 'Other']
        break
      case 'unitedStates':
        if (this.isYou()) {
          this.title = 'Do you live in the United States?'
        } else {
          this.title = `Does ${subject.normal} live in the United States?`
        }
        this.choices = ['Yes', 'No']
        break
      case 'immigrant':
        if (this.isYou()) {
          this.title = 'Are you an immigrant?'
        } else {
          this.title = `Is ${subject.normal} an immigrant?`
        }
        this.choices = ['Yes', 'No']
        break
      case 'children':
        if (this.isYou()) {
          this.title = 'How many children do you have?'
        } else {
          this.title = `How many children does ${subject.normal} have?`
        }
        this.choices = ['0', '1', '2', '3', '4+']
        if (this.isParent()) this.choices.shift()
        break
      case 'grandchildren':
        if (this.isYou()) {
          this.title = 'Do you have grandchildren?'
        } else {
          this.title = `Does ${subject.normal} have grandchildren?`
        }
        this.choices = ['Yes', 'No']
        break
      case 'numCircle':
        if (this.isYou()) {
          this.title = 'How many loved ones might you invite to your listening circle?'
        } else {
          this.title = `How many loved ones might ${subject.normal} invite to ${subject.pronounPossessive} listening circle?`
        }
        this.lineTexts = [`Recordings are private. Only those in ${subject.possessive} circle will be able to listen to ${subject.pronounPossessive} recordings, make comments, and post photos.`]
        this.choices = ['0 - will use as private diary', '1 - 5', '6+']
        break
      case 'howCaptureNow':
        this.choices = ['Video interviews', 'Audio recordings', 'Written memories']
        if (this.isYou()) {
          this.title = 'How do you preserve your life stories now?'
          this.choices.push("I don't do anything but I should.")
        } else {
          this.title = `How does ${subject.normal} preserve ${subject.pronounPossessive} life stories now?`
          this.choices.push(`I'm not sure that ${subject.pronoun} does anything right now.`)
        }
        break
      case 'technologyKnowledge':
        if (this.isYou()) {
          this.title = `How technically savvy are you?`
        } else {
          this.title = `How technically savvy is ${subject.normal}?`
        }
        this.choices = ['Expert', 'Comfortable', 'Can get by', 'Almost none!']
        break
      case 'phoneKind':
        if (this.isYou()) {
          this.title = 'What kind of phone do you use??'
        } else {
          this.title = `What kind of phone does ${subject.normal} use?`
        }
        this.lineTexts = ["StoryCircle works well with mobile phones and landlines, but you'll sign up with just one."]
        this.choices = ['Mobile phone', 'Landline', 'Both']
        break
      case 'textMessage':
        if (this.isYou()) {
          this.title = 'Do you know how to read text messages?'
        } else {
          this.title = `Does ${subject.normal} know how to read text messages?`
        }
        this.lineTexts = [`StoryCircle delivers question prompts by text message, and ${subject.normal} just calls back to record ${subject.pronounPossessive} answers after the beep, like voicemail.`]
        this.choices = ['Yes', 'No']
        break
      case 'useEmail':
        if (this.isYou()) {
          this.title = 'Do you know how to use email?'
        } else {
          this.title = `Does ${subject.normal} know how to use email?`
        }
        this.lineTexts = [`StoryCircle uses email whenever a loved one makes a comment or adds a photo to ${subject.pronounPossessive} recorded memories.`]
        this.choices = ['Yes', 'No']
        break
      case 'whiteGlove':
        this.title = `Would it be helpful to receive extra guidance?`
        this.lineTexts = [`If so, a StoryCircle onboarding expert can reach out by email or phone.`]
        this.choices = ['Yes, by email', 'Yes, by phone', `I'll ask for help if necessary`]
        break
      case 'features':
        this.title = `Which is most important to you?`
        this.choices = [`Ensuring that ${subject.possessive} recordings are private to only loved ones.`, `Augmenting ${subject.possessive} recordings with family photos and comments`, `Downloading ${subject.pronounPossessive} recordings`, `Creating an audiobook of ${subject.pronounPossessive} life stories`]
        break
      case 'freeOrPaid':
        this.title = `More likely free or premium?`
        this.lineTexts = ["You don't need to make a decision now, but we want you to know about both options."]
        if (this.isYou()) {
          this.lineTexts = this.lineTexts.concat([`<b>Free</b>: Your recordings are limited to 5 minutes each.`, `<b>Premium</b>: Your recordings can be as long as you want. $10/month.`])
        } else {
          this.lineTexts = this.lineTexts.concat([`<b>Free</b>: ${subject.possessive} recordings are limited to 5 minutes each.`, `<b>Premium</b>: ${subject.pronounPossessive} recordings can be as long as ${subject.pronoun} wants. $10/month.`])
        }
        this.choices = ['Free', 'Start with free and maybe go premium later', 'Start with premium']
        break
      case 'naturallyTalkative':
        if (this.isYou()) {
          this.title = 'Would you consider yourself to be a talkative person?'
          this.choices = [`Yes, recording my memories is going to be easy`, `Sometimes. It depends on the questions I'm posed.`, `No. I may need constant prodding and thought-provoking prompts!`]
        } else {
          this.title = `Would you consider ${subject.normal} to be a talkative person?`
          this.choices = [`Yes, recording ${subject.pronounPossessive} memories is going to be easy`, `Sometimes. It depends on the questions ${subject.pronoun} is posed.`, `No, ${subject.pronoun} may need constant prodding and thought-provoking prompts!`]
        }
        break
      case 'senseOfHumour':
        if (this.isYou()) {
          this.title = 'Do you have a good sense of humour?'
        } else {
          this.title = `Does ${subject.normal} have a good sense of humour?`
        }
        this.choices = [`Yes`, `Sometimes`, `No`]
        break

      case 'sports':
        if (this.isYou()) {
          this.title = 'Do you enjoy sports?'
        } else {
          this.title = `Does ${subject.normal} enjoy sports?`
        }
        this.choices = ['Yes', 'No']
        break
      case 'religious':
        if (this.isYou()) {
          this.title = 'Are you religious?'
        } else {
          this.title = `Is ${subject.normal} religious?`
        }
        this.choices = ['Yes', 'No']
        break
      case 'pets':
        if (this.isYou()) {
          this.title = 'Have you had pets you loved?'
        } else {
          this.title = `Has ${subject.normal} ever had pets ${subject.pronoun} loved?`
        }
        this.choices = ['Yes', 'No']
        break
      case 'college':
        this.title = `Did ${subject.normal} ever attend college?`
        this.choices = ['Yes', 'No']
        break

      case 'promptVoice':
        this.title = `Would ${subject.normal} prefer a male or female voice for ${subject.pronounPossessive} question prompts?`
        this.choices = ['Male', 'Female', 'Make it a variety']
        break
      case 'promptFrequency':
        if (this.isYou()) {
          this.title = 'How often do you want to receive question prompts?'
          this.lineTexts = ['<b>Congratulations</b>! Based on the answers you provided, we have customized your library of question prompts.', "StoryCircle will automatically send you question prompts whenever you have no pending questions from your loved ones."]
        } else {
          this.title = `How often should ${subject.normal} receive question prompts?`
          this.lineTexts = [`<b>Congratulations</b>! Based on the answers you provided, we have customized ${subject.possessive} library of question prompts.`, `StoryCircle will automatically send ${subject.normal} question prompts whenever ${subject.pronoun} has no pending questions from ${subject.pronounPossessive} loved ones.`]
        }
        this.choices = ['Once a day', 'Once a week', 'Once a month']
        break
      case 'storyTellerName':
        this.title = `What is ${subject.possessive} name?`
        this.inputs = [`First name`, `Last name`]
        break
      case 'storyTellerEmail':
        this.title = `What is ${subject.pronounPossessive} email address?`
        this.lineTexts = [`We will send an invitation to register for StoryCircle using the information you provided.`]
        this.inputs = [`${subject.possessive} email address`]
        break
      case 'storyTellerPhoneNumber':
        this.title = `What is ${subject.possessive} phone number?`
        this.lineTexts = [`Your question prompts are sent by text message.`, `Then, ${subject.normal} simply calls back the number to record ${subject.pronounPossessive} story after the beep.`]
        this.phoneInput = true
        this.allowSkip = 10000
        break
      case 'register':
        if (this.isYou()) {
          this.title = `Finish Registration`
          this.lineTexts = [`Finish your registration by entering your email address and creating a password.`]
          this.inputs = []
        } else {
          this.title = `Register an account for yourself`
          this.lineTexts = [`Before we send out ${subject.possessive} invitation email, you need to create an account for yourself.`, `You will be automatically placed in ${subject.pronounPossessive} listening circle so that you can listen to ${subject.pronounPossessive} stories.`]
          this.inputs = ['Your first name', 'Your last name']
        }
        this.inputs.push(`Your email address`)
        this.passwordInput = true
        break;
      default:
        break
    }
  }

  private showQuizStep(step: quizSteps) {
    if (this.stepShowing === step) return
    if (this.$store.state.devmode) console.log(`Activating quiz step ${step}`)
    this.modalTitle = null
    this.activeQuizComponent = quizComponents[step] || QuizUniversalComponent
    this.stepShowing = step

    this.setQuizProps(step)

    // keep history for back button
    const lastStep: quizSteps = this.stepHistory.length > 0 ? this.stepHistory[this.stepHistory.length - 1] : null
    if (lastStep !== step) this.stepHistory.push(step);

    // if (this.activeQuizComponent === QuizUniversalComponent) (this.$refs.activeQuizComponent as any).show()
    this.modalShow = true;

    (window as any).analytics.track(`Quiz ${step} Viewed`);

  }

  private async readStateAndPathAndShowCorrectStep() {
    if (this.inReadState) return
    this.inReadState = true
    if (this.$store.state.devmode) console.log(`QuizComponent.readStateAndPathAndShowCorrectStep()`)

    // check to see if user has been idle for over 10 minutes (safety check)
    const idleTimeInSec: number = (new Date().getTime() - this.lastQuizActivityTime.getTime()) / 1000
    if (idleTimeInSec > 600) {
      this.inReadState = false
      return
    }

    // sync with DB in case user is logged in and proceeding on phone
    let updatedUser: StoryUser
    if (this.userId) updatedUser = await globalUC.get(this.userId, true)
    if (updatedUser) {
      console.assert(this.userId === updatedUser.getIdAsString())           // sanity check that we're still logged in as correct user
      updatedUser.updateMeToCacheStoreAndLocalStorage()
    }

    if (!this.isDoneOrSkipped('welcome')) { this.showQuizStep('welcome'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('chooseLearningMethod')) { this.showQuizStep('chooseLearningMethod'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('whoIsStoryTeller')) { this.showQuizStep('whoIsStoryTeller'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('gender')) { this.showQuizStep('gender'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('age')) { this.showQuizStep('age'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('why')) { this.showQuizStep('why'); this.inReadState = false; return }

    if (!this.isDoneOrSkipped('speaksEnglish')) { this.showQuizStep('speaksEnglish'); this.inReadState = false; return }
    if (!this.speaksEnglish() && !this.isDoneOrSkipped('language')) { this.showQuizStep('language'); this.inReadState = false; return }
    // if (!this.isDoneOrSkipped('unitedStates')) { this.showQuizStep('unitedStates'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('immigrant')) { this.showQuizStep('immigrant'); this.inReadState = false; return }
    if (this.olderThan18() && !this.isDoneOrSkipped('children')) { this.showQuizStep('children'); this.inReadState = false; return }
    if (this.olderThan50() && this.hasChildren() && !this.isDoneOrSkipped('grandchildren')) { this.showQuizStep('grandchildren'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('numCircle')) { this.showQuizStep('numCircle'); this.inReadState = false; return }

    if (!this.isDoneOrSkipped('howCaptureNow')) { this.showQuizStep('howCaptureNow'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('technologyKnowledge')) { this.showQuizStep('technologyKnowledge'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('phoneKind')) { this.showQuizStep('phoneKind'); this.inReadState = false; return }
    if (!this.isTechnicalExpert() && !this.isDoneOrSkipped('textMessage')) { this.showQuizStep('textMessage'); this.inReadState = false; return }
    if (!this.isTechnicalExpert() && !this.isDoneOrSkipped('useEmail')) { this.showQuizStep('useEmail'); this.inReadState = false; return }
    if (this.isTechnicalChallenged() && !this.isDoneOrSkipped('whiteGlove')) { this.showQuizStep('whiteGlove'); this.inReadState = false; return }

    if (!this.isDoneOrSkipped('features')) { this.showQuizStep('features'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('freeOrPaid')) { this.showQuizStep('freeOrPaid'); this.inReadState = false; return }

    if (!this.isDoneOrSkipped('naturallyTalkative')) { this.showQuizStep('naturallyTalkative'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('senseOfHumour')) { this.showQuizStep('senseOfHumour'); this.inReadState = false; return }

    // if (this.olderThan18() && !this.isDoneOrSkipped('college')) { this.showQuizStep('college'); this.inReadState = false; return }
    // if (!this.isDoneOrSkipped('sports')) { this.showQuizStep('sports'); this.inReadState = false; return }
    // if (!this.isDoneOrSkipped('religious')) { this.showQuizStep('religious'); this.inReadState = false; return }
    // if (!this.isDoneOrSkipped('pets')) { this.showQuizStep('pets'); this.inReadState = false; return }

    if (!this.isDoneOrSkipped('promptVoice')) { this.showQuizStep('promptVoice'); this.inReadState = false; return }
    if (!this.isDoneOrSkipped('promptFrequency')) { this.showQuizStep('promptFrequency'); this.inReadState = false; return }

    if (!this.isDoneOrSkipped('storyTellerName')) { this.showQuizStep('storyTellerName'); this.inReadState = false; return }

    // champion inviting storyteller
    if (!this.isYou()) {
      if (!this.isDoneOrSkipped('storyTellerEmail')) { this.showQuizStep('storyTellerEmail'); this.inReadState = false; return }
      if (!this.isDoneOrSkipped('storyTellerPhoneNumber')) { this.showQuizStep('storyTellerPhoneNumber'); this.inReadState = false; return }
    }

    if (!this.isDoneOrSkipped('register')) { this.showQuizStep('register'); this.inReadState = false; return }

    // exit cuz nothing else to do
    this.exitQuiz()

  }



}
